/**
 * Enumerations of the weekly workout plan, copied verbatim from the mobile app
 * (PrecisionReactNative `workouts/stores/types.ts`, `workouts/stores/
 * workoutConstants.ts`, `homeTab/helpers/types.tsx`; mirrored in
 * docs/api-contracts/weekly-workout-response/enums.md). Values are what the
 * backend sends; labels are what the mobile app shows.
 */

export const WORKOUT_TYPE = {
  None: 0,
  Interval: 1,
  Resistance: 2,
  Cardio: 3,
  RestDay: 4,
} as const;

const WORKOUT_TYPE_LABELS: Record<number, string> = {
  0: "None",
  1: "Interval",
  2: "Resistance",
  3: "Cardio",
  4: "Rest Day",
};

export const workoutTypeLabel = (type: number): string =>
  WORKOUT_TYPE_LABELS[type] ?? "Unknown";

export const EXERCISE_GROUP_TYPE = {
  None: 0,
  EMOM: 1,
  Superset: 2,
  RFT: 3,
  Circuit: 4,
} as const;

const EXERCISE_GROUP_TYPE_LABELS: Record<number, string> = {
  0: "None",
  1: "EMOM",
  2: "Superset",
  3: "RFT",
  4: "Circuit",
};

export const exerciseGroupTypeLabel = (type: number): string =>
  EXERCISE_GROUP_TYPE_LABELS[type] ?? "";

/**
 * Group descriptions as the mobile `WorkoutV2GroupElement` shows them; `**`
 * is replaced by `total_minutes` (EMOM, Circuit) or `total_rounds` (RFT).
 */
export const EXERCISE_GROUP_DESCRIPTIONS: Record<number, string> = {
  0: "",
  1: "** sequences. Once you complete the reps, rest until you hit the minute.",
  2: "Rest for at least 2 minutes between each exercise.",
  3: "Complete ** rounds as quickly as possible",
  4: "Complete as many rounds as possible in ** minutes",
};

/** Exercise sub-types; the mobile cards label 1/2 as "DropSet" / "AMRAP". */
const EXERCISE_SUB_TYPE_LABELS: Record<number, string> = {
  0: "",
  1: "DropSet",
  2: "AMRAP",
};

export const exerciseSubTypeLabel = (subType: number): string =>
  EXERCISE_SUB_TYPE_LABELS[subType] ?? "";

const EQUIPMENT_LABELS: Record<number, string> = {
  0: "None",
  1: "Barbell",
  2: "Dumbbell",
  3: "Cables",
  4: "Kettlebell",
  5: "Gym Equipment",
  6: "Resistance Band",
  7: "Bodyweight",
  8: "Pull up Bar",
  9: "Yoga Mat",
  10: "Suspension Trainer",
  11: "Bike",
  12: "Treadmill",
  13: "Rower",
  14: "Elliptical",
  15: "Jumping Rope",
  16: "Boxing Equipment",
};

export const EQUIPMENT_BODYWEIGHT = 7;

export const equipmentLabel = (equipment: number): string =>
  EQUIPMENT_LABELS[equipment] ?? "";

const MUSCLE_GROUP_LABELS: Record<number, string> = {
  0: "None",
  1: "Quads Bilateral",
  2: "Quads Unilateral",
  3: "Hamstrings Bilateral",
  4: "Hamstrings Unilateral",
  5: "Glutes Bilateral",
  6: "Glutes Unilateral",
  7: "Calves Bilateral",
  8: "Calves Unilateral",
  9: "Back Lats Bilateral",
  10: "Back Lats Unilateral",
  11: "Back Mid Horizontal",
  12: "Back Mid Vertical",
  13: "Back Lower",
  14: "Back Traps",
  15: "Chest Incline Upper",
  16: "Chest Flat",
  17: "Chest Decline Lower",
  18: "Shoulders AnteriorDeltoid",
  19: "Shoulders MedialDeltoid",
  20: "Shoulders PosteriorDeltoid",
  21: "Triceps Bilateral",
  22: "Triceps Unilateral",
  23: "Biceps Bilateral",
  24: "Biceps Unilateral",
  25: "Forearms Unilateral",
  26: "Forearms Bilateral",
  27: "Abs Upper",
  28: "Abs Lower",
  29: "Abs Obliques",
  30: "Core",
};

export const muscleGroupLabel = (muscleGroup: number): string =>
  MUSCLE_GROUP_LABELS[muscleGroup] ?? "";

/**
 * Names of scheduled activities (`Activity.name`) and of wearable activity
 * types. 26 "Other Workout" means "use the entry's `canonical_name`".
 */
export const PNOE_WORKOUT_NAME: Record<number, string> = {
  1: "Cycling",
  2: "Running",
  3: "Hiking",
  4: "Swimming",
  5: "Rowing",
  6: "Martial Arts",
  7: "Play",
  8: "Pilates",
  9: "Yoga",
  10: "HIIT",
  11: "Cross Training",
  12: "Mixed Cardio",
  13: "Resistance Training",
  14: "Elliptical",
  15: "Functional Training",
  16: "Core Training",
  17: "Stair Climbing",
  18: "Jump Rope",
  19: "Boxing",
  20: "Dancing",
  21: "Housework",
  22: "Meditation",
  23: "Walking Fitness",
  24: "Weightlifting",
  25: "Mountain Biking",
  26: "Other Workout",
};

export const PNOE_WORKOUT_NAME_OTHER = 26;

/** Direction of a PNOĒ effect in the workout analysis (mobile `PNOEStatsArrowDirection`). */
export const PNOE_STATS_ARROW = {
  none: 0,
  increase: 1,
  decrease: -1,
} as const;

/** Mobile opens the analysis only for sessions with a usable heart-rate series. */
export const MIN_HR_SAMPLES_FOR_ANALYSIS = 6;
